import { useBrandLocale } from '../../../hooks/useBrandLocale';
import { mpgToLitresPer100Km } from '../../../utils/units';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faWheelchair,
  faPlay,
  faVolumeUp,
  faCamera,
  faBicycle,
  faBell,
  faHeadphones,
  faBaby,
  faCar,
  faMapPin,
  faChair,
  faKey,
  faDog,
  faSkiing,
  faSnowflake,
  faSun,
  faTicket,
  faChargingStation,
} from '@fortawesome/free-solid-svg-icons';
import { faUsb } from '@fortawesome/free-brands-svg-icons';
import { Button as BaseButton } from '../../button';
// 导入CarFeatures枚举
import { CarFeatures, GasGrade, GasType, GearType } from '../../../../__generated__/graphql';
import { useUpdateCarMutation } from '../../../services/carService/__generated__/queries.generated';
import { toast } from 'react-toastify';
import { handleInputNumberChange } from '../../../helpers/handleInputNumberChange';

// CategoryEnum will be available after running codegen
type CategoryEnum =
  | 'SUV'
  | 'MINIVAN'
  | 'SEDAN'
  | 'WAGON'
  | 'SUBRUBAN'
  | 'COUPE'
  | 'TRUCK'
  | 'CONVERTIBLE'
  | 'OTHERS';

const Container = styled.div`
  ${tw`bg-white rounded-lg p-6 w-full`}
`;

const SectionTitle = styled.h2`
  ${tw`text-2xl font-bold mb-6`}
`;

const SubSectionTitle = styled.h3`
  ${tw`text-xl font-bold mt-8 mb-4`}
`;

const FormGroup = styled.div`
  ${tw`mb-6`}
`;

const FormRow = styled.div`
  ${tw`flex flex-col md:flex-row gap-6 mb-4`}
`;

const FormColumn = styled.div`
  ${tw`flex-1 md:px-2`}

  &:first-child {
    ${tw`md:pl-0 md:pr-3`}
  }

  &:last-child {
    ${tw`md:pr-0 md:pl-3`}
  }
`;

const Label = styled.label`
  ${tw`block text-sm font-medium mb-2`}
`;

const Input = styled.input`
  ${tw`w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black`}
`;

const Select = styled.select`
  ${tw`w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black`}
`;

const FeaturesGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4`}
`;

const FeatureCard = styled.button<{ selected: boolean }>`
  ${tw`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all cursor-pointer`}
  ${props =>
    props.selected
      ? tw`bg-black border-black text-white`
      : tw`bg-white border-border-default text-text-secondary hover:border-purple-400`}

  &:hover {
    ${props => !props.selected && tw`border-purple-400 bg-bg-secondary`}
  }

  min-height: 80px;
`;

const FeatureIcon = styled.div`
  ${tw`text-2xl mb-2`}
`;

const FeatureLabel = styled.span`
  ${tw`text-sm font-medium text-center`}
`;

const Textarea = styled.textarea`
  ${tw`w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black min-h-[100px]`}
`;

const TipBox = styled.div`
  ${tw`bg-purple-100 p-4 rounded-lg mb-6`}
`;

const TipTitle = styled.div`
  ${tw`flex items-center text-sm font-medium mb-2`}
`;

const TipContent = styled.p`
  ${tw`text-sm`}
`;

const Button = styled.button`
  ${tw`bg-white border border-border-default text-text-secondary px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-black mt-2`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const CharCount = styled.div`
  ${tw`text-right text-xs text-text-secondary mt-1`}
`;

const RemoveButton = styled.button`
  ${tw`text-red-500 text-sm mt-2`}

  &:hover {
    ${tw`underline`}
  }
`;

// 特性映射，将枚举值映射到UI友好的标签和ID
const featureMapping = [
  {
    id: 'accessible',
    label: 'Accessible',
    enum: CarFeatures.WheelchairAccessible,
    icon: faWheelchair,
  },
  {
    id: 'all-wheel-drive',
    label: 'All-wheel drive',
    enum: CarFeatures.AllWheelDrive,
    icon: faCar,
  },
  { id: 'android-auto', label: 'Android Auto', enum: CarFeatures.AndroidAuto, icon: faPlay },
  {
    id: 'apple-carplay',
    label: 'Apple CarPlay',
    enum: CarFeatures.AppleCarplay,
    icon: faPlay,
  },
  { id: 'aux-input', label: 'AUX input', enum: CarFeatures.AuxInput, icon: faVolumeUp },
  {
    id: 'backup-camera',
    label: 'Backup camera',
    enum: CarFeatures.BackupCamera,
    icon: faCamera,
  },
  { id: 'bike-rack', label: 'Bike rack', enum: CarFeatures.BikeRack, icon: faBicycle },
  {
    id: 'blind-spot-warning',
    label: 'Blind spot warning',
    enum: CarFeatures.BlindSpotWarning,
    icon: faBell,
  },
  { id: 'bluetooth', label: 'Bluetooth', enum: CarFeatures.Bluetooth, icon: faHeadphones },
  { id: 'child-seat', label: 'Child seat', enum: CarFeatures.ChildSeat, icon: faBaby },
  { id: 'convertible', label: 'Convertible', enum: CarFeatures.Convertible, icon: faCar },
  { id: 'gps', label: 'GPS', enum: CarFeatures.Gps, icon: faMapPin },
  { id: 'heated-seats', label: 'Heated seats', enum: CarFeatures.HeatedSeats, icon: faChair },
  {
    id: 'keyless-entry',
    label: 'Keyless entry',
    enum: CarFeatures.KeylessEntry,
    icon: faKey,
  },
  { id: 'pet-friendly', label: 'Pet friendly', enum: CarFeatures.PetFriendly, icon: faDog },
  { id: 'ski-rack', label: 'Ski rack', enum: CarFeatures.SkiRack, icon: faSkiing },
  { id: 'snow-tires', label: 'Snow tires', enum: CarFeatures.SnowTires, icon: faSnowflake },
  { id: 'sunroof', label: 'Sunroof', enum: CarFeatures.Sunroof, icon: faSun },
  { id: 'toll-pass', label: 'Toll pass', enum: CarFeatures.TollPass, icon: faTicket },
  {
    id: 'usb-charger',
    label: 'USB charger',
    enum: CarFeatures.UsbCharger,
    icon: faChargingStation,
  },
  { id: 'usb-input', label: 'USB input', enum: CarFeatures.UsbInput, icon: faUsb },
];

interface CarDetailsComponentProps {
  car?: any;
  onFeaturesChange?: (features: CarFeatures[]) => void;
  onSave?: (carData: any) => void;
}

const CarDetailsComponent: React.FC<CarDetailsComponentProps> = ({
  car,
  onFeaturesChange,
  onSave,
}) => {
  // 基本信息状态
  const [carName, setCarName] = useState(car?.name || '');
  const [licensePlate, setLicensePlate] = useState(car?.licensePlate || '');
  const [licensePlateState, setLicensePlateState] = useState(
    car?.licensePlateState || 'California',
  );

  // 基本车辆详情状态
  const [seats, setSeats] = useState(car?.seats || 4);
  const [doors, setDoors] = useState(car?.doors || 4);
  const [fuelType, setFuelType] = useState(car?.gas || GasType.Hybrid);
  const [gearType, setGearType] = useState(car?.gearType || GearType.Automatic);
  const [gasGrade, setGasGrade] = useState(car?.gasGrade || GasGrade.Premium);
  const [cityMpg, setCityMpg] = useState(car?.cityMpg || 35);
  const [hwyMpg, setHwyMpg] = useState(car?.hwyMpg || 45);
  // Spec 028 D7: MPG is stored; a metric brand also sees litres per 100 km.
  const metric = useBrandLocale().distanceUnit === 'km';
  const litres = (mpg: number) =>
    metric && Number(mpg) > 0 ? ` (${mpgToLitresPer100Km(Number(mpg))} L/100 km)` : '';
  const [mileage, setMileage] = useState(car?.mileage || 0);
  const [category, setCategory] = useState<CategoryEnum | null>(car?.category || null);
  // 其他状态
  const [description, setDescription] = useState(car?.description || '');
  const [faqs, setFaqs] = useState<Array<{ question: string; answer: string }>>(
    car?.faqs || [{ question: '', answer: '' }],
  );

  // 特性状态
  const [selectedFeatures, setSelectedFeatures] = useState<CarFeatures[]>(car?.features || []);

  const [updateCar] = useUpdateCarMutation({
    refetchQueries: ['GetCar'],
    onCompleted: () => {
      toast.success('Car updated successfully');
    },
    onError: () => {
      toast.error('Failed to update car');
    },
  });

  // FAQ 管理函数
  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const removeFaq = (index: number) => {
    const newFaqs = [...faqs];
    newFaqs.splice(index, 1);
    setFaqs(newFaqs);
  };

  // 处理特性复选框变化
  const handleFeatureChange = (feature: CarFeatures, checked: boolean) => {
    let updatedFeatures: CarFeatures[];

    if (checked) {
      // 添加特性
      updatedFeatures = [...selectedFeatures, feature];
    } else {
      // 移除特性
      updatedFeatures = selectedFeatures.filter(f => f !== feature);
    }

    setSelectedFeatures(updatedFeatures);

    // 如果提供了回调，则调用它
    if (onFeaturesChange) {
      onFeaturesChange(updatedFeatures);
    }
  };

  // 检查特性是否被选中
  const isFeatureSelected = (feature: CarFeatures) => {
    return selectedFeatures.includes(feature);
  };

  // 保存所有更改
  const handleSave = () => {
    if (car?.id) {
      updateCar({
        variables: {
          id: car.id,
          updateCarInput: {
            name: carName,
            licensePlate,
            licensePlateState,
            seats,
            doors,
            gas: fuelType,
            gearType,
            gasGrade,
            cityMpg,
            hwyMpg,
            description,
            features: selectedFeatures,
            mileage: Number(mileage),
            ...(category && { category: category as any }),
          },
        },
      });
    }
  };

  return (
    <Container>
      <SectionTitle>Details</SectionTitle>

      <SubSectionTitle>Your car</SubSectionTitle>
      <FormGroup>
        <FormRow>
          <FormColumn>
            <Label>Car name</Label>
            <Input type="text" value={carName} onChange={e => setCarName(e.target.value)} />
          </FormColumn>
          <FormColumn>
            <Label>Car mileage (only show when car is on sale)</Label>
            <Input
              type="text"
              value={mileage}
              onChange={e => handleInputNumberChange(setMileage)(e)}
            />
          </FormColumn>
        </FormRow>
      </FormGroup>

      <FormRow>
        <FormColumn>
          <Label>License plate number</Label>
          <Input type="text" value={licensePlate} onChange={e => setLicensePlate(e.target.value)} />
        </FormColumn>
        <FormColumn>
          <Label>License plate state / province</Label>
          <Select value={licensePlateState} onChange={e => setLicensePlateState(e.target.value)}>
            <option value="California">California</option>
            <option value="New York">New York</option>
            <option value="Texas">Texas</option>
            {/* 添加更多州/省选项 */}
          </Select>
        </FormColumn>
      </FormRow>

      <SubSectionTitle>Basic car details</SubSectionTitle>
      <FormRow>
        <FormColumn>
          <Label>Number of seats</Label>
          <Select value={seats} onChange={e => setSeats(Number(e.target.value))}>
            <option value="2">2</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
          </Select>
        </FormColumn>
        <FormColumn>
          <Label>Number of doors</Label>
          <Select value={doors} onChange={e => setDoors(Number(e.target.value))}>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </Select>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Label>Fuel type</Label>
          <Select value={fuelType} onChange={e => setFuelType(e.target.value as GasType)}>
            <option value={GasType.Hybrid}>Hybrid</option>
            <option value={GasType.Gasoline}>Gas</option>
            <option value={GasType.Electric}>Electric</option>
            <option value={GasType.Diesel}>Diesel</option>
          </Select>
        </FormColumn>
        <FormColumn>
          <Label>Gas grade</Label>
          <Select value={gasGrade} onChange={e => setGasGrade(e.target.value as GasGrade)}>
            <option value={GasGrade.Premium}>Premium</option>
            <option value={GasGrade.Regular}>Regular</option>
          </Select>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Label>Transmission</Label>
          <Select value={gearType} onChange={e => setGearType(e.target.value as GearType)}>
            <option value={GearType.Automatic}>Automatic</option>
            <option value={GearType.Manual}>Manual</option>
            <option value={GearType.SemiAutomatic}>Semi-Automatic</option>
          </Select>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Label>Category</Label>
          <Select
            value={category || ''}
            onChange={e => setCategory((e.target.value as CategoryEnum) || null)}
          >
            <option value="">Select Category</option>
            <option value="SUV">SUV</option>
            <option value="MINIVAN">Minivan</option>
            <option value="SEDAN">Sedan</option>
            <option value="WAGON">Wagon</option>
            <option value="SUBRUBAN">Suburban</option>
            <option value="COUPE">Coupe</option>
            <option value="TRUCK">Truck</option>
            <option value="CONVERTIBLE">Convertible</option>
            <option value="OTHERS">Others</option>
          </Select>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Label>City MPG{litres(cityMpg)}</Label>
          <Input type="text" value={cityMpg} onChange={e => setCityMpg(Number(e.target.value))} />
        </FormColumn>
        <FormColumn>
          <Label>Hwy MPG{litres(hwyMpg)}</Label>
          <Input type="text" value={hwyMpg} onChange={e => setHwyMpg(Number(e.target.value))} />
        </FormColumn>
      </FormRow>

      <SubSectionTitle>Features</SubSectionTitle>
      <FeaturesGrid>
        {featureMapping.map(feature => (
          <FeatureCard
            key={feature.id}
            selected={isFeatureSelected(feature.enum)}
            onClick={() => handleFeatureChange(feature.enum, !isFeatureSelected(feature.enum))}
            type="button"
          >
            <FeatureIcon>
              <FontAwesomeIcon icon={feature.icon} />
            </FeatureIcon>
            <FeatureLabel>{feature.label}</FeatureLabel>
          </FeatureCard>
        ))}
      </FeaturesGrid>

      <div tw="text-xs text-text-secondary mt-2">
        Apple CarPlay is a registered trademark of Apple Inc. Android is a trademark of Google LLC.
      </div>

      <SubSectionTitle>Car description</SubSectionTitle>
      <div>
        <p tw="text-sm mb-2">
          Tell guests what makes your car unique and why they'll love driving it.
        </p>
        <a href="#" tw="text-sm text-text-secondary underline hover:text-text-primary">
          What should I include?
        </a>

        <TipBox>
          <TipTitle>
            <FontAwesomeIcon icon={faLightbulb} tw="text-text-secondary mr-2" />
            Tips to get more bookings
          </TipTitle>
          <TipContent>
            Listings with descriptions of at least 100 words are up to three times more likely to
            get booked.
          </TipContent>
        </TipBox>

        <Textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe your car..."
        />
        <CharCount>{description.length} characters remaining</CharCount>
      </div>

      <div tw="mt-6">
        <BaseButton theme="filled" text="Save Changes" onClick={handleSave} />
      </div>
    </Container>
  );
};

export default CarDetailsComponent;

// const response = await updateCar({
//     variables: {
//       id: editingCar.id,
//       updateCarInput: {
//         vin: currentVIN,
//         make,
//         model,
//         name: `${make} ${model} ${modelYear}`,
//         year: Number(modelYear),
//         seats: seats,
//         gearType,
//         carType,
//         gas: gasType,
//         licensePlate,
//         licensePlateState,
//         originalPrice: parseFloat(originalPrice),
//         gasGrade: gasGrade,
//         cityMpg: cityMpg ? parseInt(cityMpg) : null,
//         hwyMpg: hwyMpg ? parseInt(hwyMpg) : null,
//         doors: doors ? parseInt(doors) : null,
//         description: description || null,
//       },
//     },
//   });
